import { FRAME_RATE } from "../core/constants.js";

export const BRUTEFORCE_METRIC_IDS = {
  X: "x",
  Y: "y",
  VX: "vx",
  VY: "vy",
  ROTATION_VELOCITY: "rotation-velocity",
  HITBOX_RIGHT_X: "hitbox-right-x",
  HITBOX_LEFT_X: "hitbox-left-x",
  HITBOX_TOP_Y: "hitbox-top-y",
  HITBOX_BOTTOM_Y: "hitbox-bottom-y",
};

export const BRUTEFORCE_METRICS = [
  { id: BRUTEFORCE_METRIC_IDS.X, label: "X", shortLabel: "X", axis: "horizontal" },
  { id: BRUTEFORCE_METRIC_IDS.Y, label: "Y", shortLabel: "Y", axis: "vertical" },
  { id: BRUTEFORCE_METRIC_IDS.VX, label: "Velocity X", shortLabel: "VX", axis: "horizontal" },
  { id: BRUTEFORCE_METRIC_IDS.VY, label: "Velocity Y", shortLabel: "VY", axis: "vertical" },
  { id: BRUTEFORCE_METRIC_IDS.ROTATION_VELOCITY, label: "Rotation Velocity", shortLabel: "Rot Vel", axis: "rotation" },
  { id: BRUTEFORCE_METRIC_IDS.HITBOX_RIGHT_X, label: "Right Edge X", shortLabel: "Right X", axis: "horizontal" },
  { id: BRUTEFORCE_METRIC_IDS.HITBOX_LEFT_X, label: "Left Edge X", shortLabel: "Left X", axis: "horizontal" },
  { id: BRUTEFORCE_METRIC_IDS.HITBOX_TOP_Y, label: "Top Edge Y", shortLabel: "Top Y", axis: "vertical" },
  { id: BRUTEFORCE_METRIC_IDS.HITBOX_BOTTOM_Y, label: "Bottom Edge Y", shortLabel: "Bottom Y", axis: "vertical" },
];

export const BRUTEFORCE_DIRECTION_IDS = {
  INCREASE: "increase",
  DECREASE: "decrease",
  CONVERGE: "converge",
};

export const BRUTEFORCE_DIRECTION_LABELS = {
  [BRUTEFORCE_DIRECTION_IDS.INCREASE]: "Increase",
  [BRUTEFORCE_DIRECTION_IDS.DECREASE]: "Decrease",
  [BRUTEFORCE_DIRECTION_IDS.CONVERGE]: "Converge",
};

export const BRUTEFORCE_CONDITION_TYPES = {
  AT_LEAST: "at-least",
  AT_MOST: "at-most",
};

export const BRUTEFORCE_CONDITION_TYPE_LABELS = {
  [BRUTEFORCE_CONDITION_TYPES.AT_LEAST]: "At Least",
  [BRUTEFORCE_CONDITION_TYPES.AT_MOST]: "At Most",
};

export const DEFAULT_BRUTEFORCE_TARGET = {
  metric: BRUTEFORCE_METRIC_IDS.X,
  direction: BRUTEFORCE_DIRECTION_IDS.INCREASE,
  value: "0",
};

export const DEFAULT_BRUTEFORCE_CONDITION = {
  metric: BRUTEFORCE_METRIC_IDS.Y,
  type: BRUTEFORCE_CONDITION_TYPES.AT_LEAST,
  value: "0",
};

export const BRUTEFORCE_MUTATION_ACTIONS = [
  { id: "left", label: "Left" },
  { id: "right", label: "Right" },
  { id: "up", label: "Up" },
];

function createDefaultMutationAction({ maxCount = 0, deltaMinFrames = null, deltaMaxFrames = null } = {}) {
  const result = {
    minCount: 0,
    maxCount,
    minTime: "0:0",
    maxTime: "0",
  };
  if (typeof deltaMinFrames === "number" && typeof deltaMaxFrames === "number") {
    result.deltaMinFrames = deltaMinFrames;
    result.deltaMaxFrames = deltaMaxFrames;
  }
  return result;
}

function createDefaultMutationConfig(baseConfig) {
  return Object.fromEntries(BRUTEFORCE_MUTATION_ACTIONS.map((action) => [action.id, createDefaultMutationAction(baseConfig)]));
}

function normalizeTimeToken(value, fallback) {
  if (typeof value !== "string") {
    return fallback;
  }
  const trimmed = value.trim();
  return trimmed || fallback;
}

export const DEFAULT_BRUTEFORCE_SETTINGS = {
  levelId: 1,
  target: { ...DEFAULT_BRUTEFORCE_TARGET },
  targetTime: "3:4",
  conditions: [],
  mutations: {
    modify: createDefaultMutationConfig({ maxCount: 1, deltaMinFrames: -FRAME_RATE, deltaMaxFrames: FRAME_RATE }),
    add: createDefaultMutationConfig({ maxCount: 1 }),
    delete: createDefaultMutationConfig({ maxCount: 1 }),
  },
};

function normalizeMutationAction(source, defaults) {
  const resolvedSource = source && typeof source === "object" ? source : {};
  const minCount = Math.max(0, Number(resolvedSource.minCount ?? defaults.minCount) || 0);
  const maxCount = Math.max(minCount, Number(resolvedSource.maxCount ?? defaults.maxCount) || 0);
  const result = {
    minCount,
    maxCount,
    minTime: normalizeTimeToken(resolvedSource.minTime, defaults.minTime),
    maxTime: normalizeTimeToken(resolvedSource.maxTime, defaults.maxTime),
  };
  if (typeof defaults.deltaMinFrames === "number") {
    const deltaMinFrames = Math.floor(Number(resolvedSource.deltaMinFrames ?? defaults.deltaMinFrames) || 0);
    const deltaMaxFrames = Math.floor(Number(resolvedSource.deltaMaxFrames ?? defaults.deltaMaxFrames) || 0);
    result.deltaMinFrames = Math.min(deltaMinFrames, deltaMaxFrames);
    result.deltaMaxFrames = Math.max(deltaMinFrames, deltaMaxFrames);
  }
  return result;
}

function normalizeMutationConfig(source, defaults, legacyOperation = null) {
  const resolvedSource = source && typeof source === "object" ? source : {};
  const resolvedLegacy = legacyOperation && typeof legacyOperation === "object" ? legacyOperation : null;
  const legacyActions = resolvedLegacy?.actions && typeof resolvedLegacy.actions === "object" ? resolvedLegacy.actions : null;
  return Object.fromEntries(BRUTEFORCE_MUTATION_ACTIONS.map((action) => {
    const actionDefaults = defaults[action.id];
    const explicitAction = resolvedSource[action.id];
    const fallbackAction = explicitAction == null && resolvedLegacy
      ? {
        minCount: resolvedLegacy.minCount,
        maxCount: legacyActions && legacyActions[action.id] === false ? 0 : resolvedLegacy.maxCount,
        minTime: resolvedLegacy.minTime,
        maxTime: resolvedLegacy.maxTime,
        deltaMinFrames: resolvedLegacy.deltaMinFrames,
        deltaMaxFrames: resolvedLegacy.deltaMaxFrames,
      }
      : null;
    return [action.id, normalizeMutationAction(explicitAction ?? fallbackAction, actionDefaults)];
  }));
}

export function getBruteforceMetric(metricId) {
  return BRUTEFORCE_METRICS.find((metric) => metric.id === metricId) ?? BRUTEFORCE_METRICS[0];
}

export function getBruteforceMetricValue(telemetry, metricId) {
  switch (metricId) {
    case BRUTEFORCE_METRIC_IDS.X:
      return telemetry.x;
    case BRUTEFORCE_METRIC_IDS.Y:
      return telemetry.y;
    case BRUTEFORCE_METRIC_IDS.VX:
      return telemetry.vx;
    case BRUTEFORCE_METRIC_IDS.VY:
      return telemetry.vy;
    case BRUTEFORCE_METRIC_IDS.ROTATION_VELOCITY:
      return telemetry.rotationVelocity;
    case BRUTEFORCE_METRIC_IDS.HITBOX_RIGHT_X:
      return telemetry.hitboxRightX;
    case BRUTEFORCE_METRIC_IDS.HITBOX_LEFT_X:
      return telemetry.hitboxLeftX;
    case BRUTEFORCE_METRIC_IDS.HITBOX_TOP_Y:
      return telemetry.hitboxTopY;
    case BRUTEFORCE_METRIC_IDS.HITBOX_BOTTOM_Y:
      return telemetry.hitboxBottomY;
    default:
      return telemetry.x;
  }
}

export function defaultBruteforceSettings() {
  return {
    ...DEFAULT_BRUTEFORCE_SETTINGS,
    target: { ...DEFAULT_BRUTEFORCE_TARGET },
    conditions: [],
    levelId: DEFAULT_BRUTEFORCE_SETTINGS.levelId,
    mutations: {
      modify: normalizeMutationConfig(null, DEFAULT_BRUTEFORCE_SETTINGS.mutations.modify),
      add: normalizeMutationConfig(null, DEFAULT_BRUTEFORCE_SETTINGS.mutations.add),
      delete: normalizeMutationConfig(null, DEFAULT_BRUTEFORCE_SETTINGS.mutations.delete),
    },
  };
}

export function normalizeBruteforceCondition(value) {
  const source = value && typeof value === "object" ? value : {};
  return {
    metric: BRUTEFORCE_METRICS.some((metric) => metric.id === source.metric)
      ? source.metric
      : DEFAULT_BRUTEFORCE_CONDITION.metric,
    type: Object.values(BRUTEFORCE_CONDITION_TYPES).includes(source.type)
      ? source.type
      : DEFAULT_BRUTEFORCE_CONDITION.type,
    value: typeof source.value === "string" ? source.value : DEFAULT_BRUTEFORCE_CONDITION.value,
  };
}

function normalizeLegacyConditions(source) {
  const conditions = [];
  const minTargetY = typeof source.minTargetY === "string" ? source.minTargetY.trim() : "";
  if (minTargetY && minTargetY !== "0") {
    conditions.push({
      metric: BRUTEFORCE_METRIC_IDS.Y,
      type: BRUTEFORCE_CONDITION_TYPES.AT_LEAST,
      value: minTargetY,
    });
  }
  const maxTargetY = typeof source.maxTargetY === "string" ? source.maxTargetY.trim() : "";
  if (maxTargetY && maxTargetY !== "0") {
    conditions.push({
      metric: BRUTEFORCE_METRIC_IDS.Y,
      type: BRUTEFORCE_CONDITION_TYPES.AT_MOST,
      value: maxTargetY,
    });
  }
  return conditions;
}

function normalizeLegacyTarget(source) {
  if (source.targetType === "max-hitbox-right-edge-x-at-time") {
    return {
      metric: BRUTEFORCE_METRIC_IDS.HITBOX_RIGHT_X,
      direction: BRUTEFORCE_DIRECTION_IDS.INCREASE,
      value: DEFAULT_BRUTEFORCE_TARGET.value,
    };
  }
  return {
    metric: BRUTEFORCE_METRIC_IDS.X,
    direction: BRUTEFORCE_DIRECTION_IDS.INCREASE,
    value: DEFAULT_BRUTEFORCE_TARGET.value,
  };
}

export function normalizeBruteforceSettings(value) {
  const defaults = defaultBruteforceSettings();
  const source = value && typeof value === "object" ? value : {};
  const targetSource = source.target && typeof source.target === "object"
    ? source.target
    : normalizeLegacyTarget(source);
  const conditionsSource = Array.isArray(source.conditions)
    ? source.conditions
    : normalizeLegacyConditions(source);
  const mutationsSource = source.mutations && typeof source.mutations === "object"
    ? source.mutations
    : {
      modify: { maxCount: source.maxModifiedInputs, minTime: source.minTime, maxTime: source.maxTime },
      add: { maxCount: source.maxAddedInputs, minTime: source.minTime, maxTime: source.maxTime },
      delete: { maxCount: source.maxDeletedInputs, minTime: source.minTime, maxTime: source.maxTime },
    };
  return {
    levelId: Math.max(1, Number(source.levelId ?? defaults.levelId) || defaults.levelId),
    target: {
      metric: BRUTEFORCE_METRICS.some((metric) => metric.id === targetSource.metric)
        ? targetSource.metric
        : defaults.target.metric,
      direction: Object.values(BRUTEFORCE_DIRECTION_IDS).includes(targetSource.direction)
        ? targetSource.direction
        : defaults.target.direction,
      value: typeof targetSource.value === "string" ? targetSource.value : defaults.target.value,
    },
    targetTime: normalizeTimeToken(source.targetTime, defaults.targetTime),
    conditions: conditionsSource.map((condition) => normalizeBruteforceCondition(condition)),
    mutations: {
      modify: normalizeMutationConfig(mutationsSource.modify, defaults.mutations.modify, mutationsSource.modify),
      add: normalizeMutationConfig(mutationsSource.add, defaults.mutations.add, mutationsSource.add),
      delete: normalizeMutationConfig(mutationsSource.delete, defaults.mutations.delete, mutationsSource.delete),
    },
    scriptText: typeof source.scriptText === "string" ? source.scriptText : "",
  };
}
